import { init, reactRouterV6BrowserTracingIntegration, replayIntegration } from "@sentry/react";
import { useEffect } from "react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router";

import { Environment } from "../common/Constants";

import { ENVIRONMENT, SENTRY_ENABLE } from "./environment";

export function initSentry() {
  // noinspection JSUnusedGlobalSymbols
  init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    enabled: SENTRY_ENABLE === "true",
    integrations: [
      reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: ENVIRONMENT === Environment.PRODUCTION ? 1 : 1,
    tracePropagationTargets: ["localhost", /^https:\/\/test\.octalog\.de/, /^https:\/\/app\.octalog\.de/],

    // Error filtering
    beforeSend(event) {
      // Filter out common non-actionable errors
      if (event.exception?.values?.[0]?.value?.includes("Non-Error promise rejection")) {
        return null;
      }
      if (event.exception?.values?.[0]?.value?.includes("Script error")) {
        return null;
      }
      // Filter out network errors from ad blockers, extensions, etc.
      if (event.exception?.values?.[0]?.type === "ChunkLoadError") {
        return null;
      }
      return event;
    },

    // Session Replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: ENVIRONMENT === Environment.PRODUCTION ? 1 : 1,

    // Environment
    environment: ENVIRONMENT,

    release: import.meta.env.VITE_SENTRY_RELEASE,
  });
}

export function setSentryContext(userId: string, tenantId: string) {
  import("@sentry/react").then(({ setUser, setTag, setContext, addBreadcrumb }) => {
    // Set user context (for individual user info)
    setUser({
      id: userId,
    });

    // Set tenant as tag (for filtering/searching in Sentry UI)
    setTag("tenantId", tenantId);

    // Set tenant as context (for structured tenant details)
    setContext("tenant", {
      id: tenantId,
    });

    // Add breadcrumb for context tracking
    addBreadcrumb({
      category: "auth",
      message: `User context set - userId: ${userId}, tenantId: ${tenantId}`,
      level: "info",
      data: {
        userId,
        tenantId,
      },
    });
  });
}

export function clearSentryContext() {
  import("@sentry/react").then(({ setUser, setTag, setContext, addBreadcrumb }) => {
    // Clear user context
    setUser(null);

    // Clear tenant tag and context
    setTag("tenantId", null);
    setContext("tenant", null);

    // Add breadcrumb for logout tracking
    addBreadcrumb({
      category: "auth",
      message: "User context cleared - user logged out",
      level: "info",
    });
  });
}
